import { Page } from "../enums/Page";
import { css, htmlArray } from "../helpers/webComponents";
/** CSS affecting the {@link RegisterComponent} */
const registerStyles: string = css`
:host {
    font-family: "Onesize";
    width: 100%;
    max-width: 1024px;
    height: 100vh; /* Full screen height */
    display: flex;
    align-items: center; /* Vertically center the form */
    justify-content: center; /* Horizontally center the form */
    flex-direction: column; /* Position title above the form */
}

/** Styles for the title */
.title {
    text-align: center;
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 20px; /* Space between title and form */
    color: white;
}

/** Centered form with enhanced styling */
.content {
    width: 90%;
    max-width: 600px;
    padding: 30px;
    background-color: #666666;
    opacity: 0.95;
    border: 10px solid #4e4e4e;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center; /* Ensures content remains centered */
}

/** Style for the form itself */
.content form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/** More visible labels */
.content label {
    margin: 10px 0;
    font-size: 18px;
    color: white;
}

/** Larger input fields */
.content input {
    padding: 12px;
    width: 100%;
    font-size: 18px;
    border: 2px solid #4e4e4e;
    background-color: #3c3c3c;
    color: white;
    text-align: center;
    border-radius: 5px;
}

/** Success message styling */
.message.success {
    color: #32e113;
}

/** Error message styling */
.message.error {
    color: red;
}

/** Button styling */
.return-button,
.register-button {
    font-family: "Onesize", sans-serif;
    font-size: 20px; /* Larger font size */
    background-color: #780606;
    border: 1px solid #6d6d6d;
    padding: 15px 20px;
    margin-top: 20px;
    text-transform: uppercase;
    cursor: pointer;
    width: 100%;
    text-align: center;
    color: white;
    border-radius: 5px;
    transition: background-color 0.01s ease-in-out;
}

/** Hover effect for buttons */
.return-button:hover,
.register-button:hover {
    background-color: #bebbbb;
    color: black;
}
`;

type Data = {
    message: string;
};

export class RegisterComponent extends HTMLElement {
    /**
     * Lifecycle method triggered when the component is attached to the DOM.
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        this.render();
    }

    /**
     * Renders the component content inside the shadow DOM.
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${registerStyles}
            </style>

            ${this.renderTitle()}
            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
        this.addFormListener();

        const returnButton: Element | null = this.shadowRoot.querySelector(".return-button");
        returnButton?.addEventListener("click", () => this.returnToRoom());
    }

    /**
     * Renders the title element.
     *
     * @returns {string} HTML string for the title.
     */
    private renderTitle(): string {
        return "<div class=\"title\">Register</div>";
    }

    /**
     * Renders the content section, including the registration form.
     *
     * @returns {string} HTML string for the content section.
     */
    private renderContent(): string {
        return `
            <div class="content">
                <form id="register-form">
                    <label for="username">Username:</label>
                    <input type="text" id="username" name="username" required>

                    <label for="password">Password:</label>
                    <input type="password" id="password" name="password" required>

                    <label for="email">E-mail:</label>
                    <input type="email" id="email" name="email" required>

                    <button type="submit" class="register-button">Register</button>
                    <button class="return-button">Return to the game</button>
                </form>
                <div id="message" class="message"></div>
            </div>
        `;
    }

    /**
     * Adds an event listener to the registration form to handle submission.
     */
    private addFormListener(): void {
        if (!this.shadowRoot) return;

        const form: HTMLFormElement | null = this.shadowRoot.querySelector("#register-form");
        const messageDiv: HTMLElement | null = this.shadowRoot.querySelector("#message");

        if (!form || !messageDiv) return;

        form.addEventListener("submit", async event => {
            event.preventDefault();

            type RegisterRequestBody = {
                username: string;
                password: string;
                email: string;
                playerSessionId: string;
                playersessionJSON: string;
            };

            const playerSessionId: string = localStorage.getItem("playerSessionId") || crypto.randomUUID();
            localStorage.setItem("playerSessionId", playerSessionId);

            const playersessionJSON: string = JSON.stringify(localStorage.getItem("playerSessionId"));

            const formData: FormData = new FormData(form);
            const requestBody: RegisterRequestBody = {
                username: formData.get("username") as string,
                password: formData.get("password") as string,
                email: formData.get("email") as string,
                playerSessionId: playerSessionId,
                playersessionJSON: playersessionJSON,
            };

            const passwordIsValid: boolean = this.checkPasswordStrength(requestBody.password);
            if (!passwordIsValid) {
                messageDiv.textContent = "Password is not strong enough. It must be at least 8 characters long, contain a number, an uppercase letter, and a special character.";
                messageDiv.className = "message error";
                return;
            }

            const emailIsValid: boolean = this.checkEmailValidity(requestBody.email);
            if (!emailIsValid) {
                messageDiv.textContent = "Invalid email format.";
                messageDiv.className = "message error";
                return;
            }

            try {
                const response: Response = await fetch(`${VITE_API_URL}milad/register`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(requestBody),
                });

                if (response.ok) {
                    localStorage.setItem("userSession", "Active");
                }

                if (!response.ok) {
                    throw new Error("Registration error");
                }

                const data: Data = await response.json() as Data;

                messageDiv.textContent = data.message || "Registration is successful!";
                messageDiv.className = "message success";
            }
            catch (error) {
                messageDiv.textContent = "Registration error: " + (error instanceof Error ? error.message : "unknown error");
                messageDiv.className = "message error";
            }
        });
    }

    private checkPasswordStrength(password: string): boolean {
        const minLength: number = 8;
        const regex: RegExp = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).+$/;

        return password.length >= minLength && regex.test(password);
    }

    private checkEmailValidity(email: string): boolean {
        const emailRegex: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Handles returning to the game by dispatching a custom event.
     */
    private returnToRoom(): void {
        const event: CustomEvent = new CustomEvent("game-switch-page", {
            detail: { page: Page.Canvas },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }
}
